import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, updateDoc, Timestamp } from "firebase/firestore";
import { firestore } from "../FirebaseConfig";
import { useAuth } from "../contexts/CombinedAuthContext";
// Import isTraveler, isTravelerPlus, isBusinessOwner from usePlans
import { usePlans } from "../contexts/PlansContext";
import { useAdmin } from "../contexts/AdminContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSkull,
  faBell,
  faBars,
  faTimes,
  faStore,
  faCoins,
  faUserCog,
  faLifeRing,
  faShieldAlt,
  faMapMarkedAlt,
  faCalendarAlt,
  faTicketAlt,
  faBriefcase,
  faQuestionCircle,
  faEnvelope,
  faChartLine,
  faCompass,
  faChevronDown,
  faHome,
  faShoppingCart,
} from "@fortawesome/free-solid-svg-icons";
import TokenBalance from "./TokenBalance";
import usePromotionCartCount from "../hooks/usePromotionCartCount";
import "../styles/Header.css";

function Header() {
  // Get the entire auth context
  const auth = useAuth();
  const {
    user,
    login,
    signup,
    logout,
    isAuthenticated,
    firebaseUser,
    // firebaseConnected,
    // getConnectionDuration,
    // formatExpiryTime,
    // refreshFirebaseToken,
  } = auth;

  // Get plans context to check subscription types
  // Destructure the check functions directly
  const { isTraveler, isTravelerPlus, isBusinessOwner } = usePlans();

  // Get admin context to check admin status
  const { isAdmin, unreadAlertCount, alerts, hasPermission } = useAdmin();
  const navigate = useNavigate();

  // Local state for dropdown and mobile menu
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Desktop nav: label of the group whose panel is open (or null)
  const [openGroup, setOpenGroup] = useState(null);
  // Mobile nav: set of group labels currently expanded
  const [openMobileGroups, setOpenMobileGroups] = useState(() => new Set());

  // Promotion-cart badge — live count via shared hook.
  const promotionCartCount = usePromotionCartCount(firebaseUser?.uid);

  // Toggle dropdown on user icon click
  const toggleDropdown = () => setShowDropdown((prev) => !prev);

  // Close user dropdown + any open desktop nav group when clicking outside
  const dropdownRef = useRef(null);
  const navRef = useRef(null);
  const bellRef = useRef(null);
  const [showNotifications, setShowNotifications] = useState(false);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenGroup(null);
      }
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Escape key closes any open nav group or user dropdown
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setOpenGroup(null);
        setShowDropdown(false);
        setShowNotifications(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  // A super-admin sees a live count of unacknowledged alerts here; other admin
  // tiers see the bell only if they hold alerts.view (rules gate the data too).
  const canSeeAlerts =
    isAdmin && typeof hasPermission === "function" && hasPermission("alerts.view");
  const topAlerts = Array.isArray(alerts) ? alerts.slice(0, 10) : [];

  // Where a notification navigates: its own link, else a category default.
  const alertLink = (a) =>
    a.link || (a.category === "security" ? "/admin/security" : "/admin/alerts");

  const openAlert = async (a) => {
    setShowNotifications(false);
    if (hasPermission("alerts.acknowledge") && !a.acknowledged) {
      try {
        await updateDoc(doc(firestore, "systemAlerts", a.id), {
          acknowledged: true,
          acknowledgedAt: Timestamp.now(),
        });
      } catch (err) {
        console.error("[Header] acknowledge alert failed:", err);
      }
    }
    navigate(alertLink(a));
  };

  // Hamburger menu toggle
  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);

  const toggleGroup = useCallback((label) => {
    setOpenGroup((prev) => (prev === label ? null : label));
  }, []);

  const toggleMobileGroup = useCallback((label) => {
    setOpenMobileGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    setOpenMobileGroups(new Set());
  }, []);

  // Function to get a display name for the user
  const getUserDisplayName = () => {
    if (!user) return "Guest";
    if (user.member && user.member.profile && user.member.profile.nickname) {
      return user.member.profile.nickname;
    }
    if (user.member && user.member.email) {
      return user.member.email.split("@")[0];
    }
    return "Member";
  };

  // Get user email
  const getUserEmail = () => {
    if (!user || !user.member) return "";
    return user.member.email || user.member.loginEmail || "";
  };

  // Use the functions from the context directly
  const hasTravelerSubscription = isTraveler();
  const hasTravelerPlusSubscription = isTravelerPlus();
  const hasBusinessSubscription = isBusinessOwner();

  // V2 navigation: 5 top-level groups. HOME is a direct link; the others are
  // click-to-open dropdowns. Auth-only items (Account, Roommate Finder, etc.)
  // live in the right-side user dropdown.
  const navGroups = [
    { kind: "link", label: "HOME", path: "/" },
    {
      kind: "group",
      label: "DISCOVER",
      icon: faCompass,
      items: [
        { label: "Deals", path: "/business-listings", icon: faStore },
        { label: "Treasure Map", path: "/pirate-map", icon: faMapMarkedAlt },
        { label: "Events", path: "/events", icon: faCalendarAlt },
        { label: "Real Estate", path: "/properties", icon: faHome },
      ],
    },
    {
      kind: "group",
      label: "MEMBERSHIP",
      icon: faTicketAlt,
      items: [
        { label: "How It Works", path: "/how-it-works", icon: faQuestionCircle },
        { label: "Traveler Passes", path: "/traveler-passes", icon: faTicketAlt },
        { label: "Doubloons", path: "/doubloons", icon: faCoins },
      ],
    },
    {
      kind: "group",
      label: "FOR BUSINESSES",
      icon: faBriefcase,
      items: [
        { label: "Why List", path: "/for-businesses", icon: faBriefcase },
        { label: "Real Estate Brokers", path: "/for-brokers", icon: faHome },
        {
          label: "Business Dashboard",
          path: "/business-dashboard",
          icon: faChartLine,
          protected: true,
          requireBusiness: true,
        },
      ],
    },
    {
      kind: "group",
      label: "HELP",
      icon: faLifeRing,
      items: [
        { label: "Contact", path: "/contact", icon: faEnvelope },
        { label: "Help & Support", path: "/support", icon: faLifeRing },
        { label: "Staff Sign-In", path: "/staff/login", icon: faUserCog },
      ],
    },
  ];

  // Filter leaf items by auth/business gates.
  const isItemVisible = (item) => {
    if (item.protected && !isAuthenticated) return false;
    if (item.requireBusiness && !hasBusinessSubscription) return false;
    return true;
  };

  // Drop groups that end up with zero visible items.
  const visibleGroups = navGroups
    .map((g) =>
      g.kind === "group"
        ? { ...g, items: g.items.filter(isItemVisible) }
        : g
    )
    .filter((g) => g.kind !== "group" || g.items.length > 0);

  return (
    <div className="top-navigation">
      <div style={{ display: "flex", alignItems: "center" }}>
        {/* Hamburger icon for mobile */}
        <span className="hamburger" onClick={toggleMobileMenu}>
          <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
        </span>

        {/* Standard navigation links (hidden on mobile via CSS).
            R5-M1 jumpnav styling applied via .r5-jumpnav. */}
        <nav
          id="main-navigation"
          ref={navRef}
          className="main-nav r5-jumpnav"
          aria-label="Main navigation"
        >
          <ul>
            {visibleGroups.map((entry) => {
              if (entry.kind === "link") {
                // Render with the SAME class list as the dropdown triggers
                // below so HOME shares the trigger's box geometry exactly.
                // Stays an <a> (via <Link>) so middle-click / copy-link /
                // screen-reader "link" semantics are preserved.
                return (
                  <li key={entry.label}>
                    <Link to={entry.path} className="nav-group__trigger r5-jumpnav__link">
                      {entry.label}
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="nav-group__chevron nav-group__chevron--placeholder"
                        aria-hidden="true"
                      />
                    </Link>
                  </li>
                );
              }
              const panelId = `nav-group-${entry.label.replace(/\s+/g, "-").toLowerCase()}`;
              const isOpen = openGroup === entry.label;
              return (
                <li
                  key={entry.label}
                  className="nav-group"
                >
                  <button
                    type="button"
                    className="nav-group__trigger r5-jumpnav__link"
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleGroup(entry.label)}
                  >
                    {entry.label}
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`nav-group__chevron${isOpen ? " is-open" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <ul
                      id={panelId}
                      role="menu"
                      className="nav-group__panel"
                      aria-label={entry.label}
                      onMouseLeave={() => setOpenGroup(null)}
                    >
                      {entry.items.map((item) => (
                        <li key={item.path} role="none">
                          <Link
                            to={item.path}
                            role="menuitem"
                            className="nav-group__item"
                            onClick={() => setOpenGroup(null)}
                          >
                            {item.icon && (
                              <FontAwesomeIcon
                                icon={item.icon}
                                style={{ marginRight: "8px", width: "16px" }}
                              />
                            )}
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Right-side user controls */}
      <div
        className="user-controls"
        style={{
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Token Balance Display - only for authenticated users */}
        {isAuthenticated && user?.member?.id && (
          <div className="token-balance-wrapper" style={{ marginRight: "12px" }}>
            <TokenBalance userId={user.member.id} />
          </div>
        )}

        {/* Promotion cart badge — only shown when items are queued */}
        {isAuthenticated && promotionCartCount > 0 && (
          <Link
            to="/promotions/cart"
            className="promo-cart-icon"
            aria-label={`Promotion cart (${promotionCartCount} item${promotionCartCount === 1 ? "" : "s"})`}
          >
            <FontAwesomeIcon icon={faShoppingCart} />
            <span aria-hidden="true" className="promo-cart-icon__badge">
              {promotionCartCount}
            </span>
          </Link>
        )}

        {/* Bell icon — functional alert center for admins with alerts.view;
            a plain decorative bell for everyone else (unchanged). */}
        {canSeeAlerts ? (
          <div className="notif-bell" ref={bellRef} style={{ marginRight: "15px" }}>
            <button
              type="button"
              className="notif-bell__btn"
              aria-label={`Notifications (${unreadAlertCount} unread)`}
              aria-expanded={showNotifications}
              onClick={() => setShowNotifications((v) => !v)}
            >
              <FontAwesomeIcon icon={faBell} />
              {unreadAlertCount > 0 && (
                <span aria-hidden="true" className="notif-bell__badge">
                  {unreadAlertCount > 99 ? "99+" : unreadAlertCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="notif-dropdown" role="menu">
                <div className="notif-dropdown__head">
                  <span>Notifications</span>
                  <Link
                    to="/admin/alerts"
                    className="notif-dropdown__all"
                    onClick={() => setShowNotifications(false)}
                  >
                    View all
                  </Link>
                </div>
                {topAlerts.length === 0 ? (
                  <div className="notif-empty">You're all caught up.</div>
                ) : (
                  topAlerts.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      role="menuitem"
                      className={`notif-item notif-item--${a.severity || "info"}${
                        a.acknowledged ? " notif-item--read" : ""
                      }`}
                      onClick={() => openAlert(a)}
                    >
                      <span className="notif-item__dot" aria-hidden="true" />
                      <span className="notif-item__body">
                        <span className="notif-item__title">{a.title || a.type}</span>
                        {a.message && (
                          <span className="notif-item__msg">{a.message}</span>
                        )}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="notification-icon" style={{ marginRight: "15px" }}>
            <FontAwesomeIcon icon={faBell} />
          </div>
        )}

        {/* User icon and dropdown wrapped in a container */}
        <div
          ref={dropdownRef}
          className="user-menu-container"
          onMouseEnter={() => setShowDropdown(true)}
          onMouseLeave={() => setShowDropdown(false)}
        >
          {/* User icon triggers dropdown */}
          <div
            className="user-profile"
            onClick={toggleDropdown}
            style={{ cursor: "pointer" }}
          >
            <FontAwesomeIcon icon={faSkull} className="user-skull" />
          </div>

          {/* Dropdown menu for user details */}
          {showDropdown && (
            <div className="user-dropdown">
              {isAuthenticated ? (
                <>
                  <div
                    className="dropdown-info"
                    style={{ marginBottom: "10px" }}
                  >
                    <p>Welcome, {getUserDisplayName()}!</p>
                    <p>{getUserEmail()}</p>

                    {/* Show subscription badges using the context functions */}
                    <div className="role-badge-row">
                      {hasTravelerSubscription && (
                        <span className="role-badge role-badge--traveler">
                          Traveler
                        </span>
                      )}
                      {hasTravelerPlusSubscription && (
                        <span className="role-badge role-badge--traveler">
                          Traveler Plus
                        </span>
                      )}
                      {hasBusinessSubscription && (
                        <span className="role-badge role-badge--business">
                          Business
                        </span>
                      )}
                      {isAdmin && (
                        <span className="role-badge role-badge--admin">
                          Admin
                        </span>
                      )}
                    </div>
                  </div>
                  <Link to="/account">My Account</Link>
                  <Link to="/account/subscriptions">Subscriptions</Link>
                  <Link to="/roommate-finder">Roommate Finder</Link>
                  {hasBusinessSubscription && (
                    <>
                      <Link to="/account/business">Business Profile</Link>
                      <Link to="/account/analytics">Business Analytics</Link>
                      <Link to="/business-dashboard">Business Dashboard</Link>
                    </>
                  )}
                  {isAdmin && (
                    <Link to="/admin" className="admin-link">
                      <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: "8px" }} />
                      Admin Dashboard
                      {unreadAlertCount > 0 && (
                        <span className="admin-badge">{unreadAlertCount}</span>
                      )}
                    </Link>
                  )}
                  <button onClick={logout} className="logout-button">
                    Logout
                  </button>
                </>
              ) : (
                <div className="auth-buttons">
                  <button onClick={login} className="login-button">
                    Login
                  </button>
                  <button onClick={signup} className="signup-button">
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Firebase skeleton icon positioned to the right of user icon */}
        {/* <div style={{ marginLeft: "15px" }}>
          <div className="firebase-status" title="Firebase connection status">
            {firebaseConnected ? (
              <div className="connection-status connected">
                <FontAwesomeIcon
                  icon={faSkull}
                  className="status-icon connected"
                />
                <div className="status-tooltip">
                  <p>
                    <strong>Firebase Connected</strong>
                  </p>
                  <p>User: {firebaseUser?.email || "Unknown"}</p>
                  <p>Connected for: {getConnectionDuration()}</p>
                  <p>Token expires: {formatExpiryTime()}</p>
                </div>
              </div>
            ) : (
              <div
                className="firebase-status-controls"
                onClick={refreshFirebaseToken}
                title="Click to reconnect to Firebase"
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon
                  icon={faSkull}
                  className="status-icon disconnected"
                />
              </div>
            )}
          </div>
        </div> */}
      </div>

      {/* Mobile navigation menu */}
      {mobileMenuOpen && (
        <nav className="mobile-nav" aria-label="Mobile navigation">
          {visibleGroups.map((entry) => {
            if (entry.kind === "link") {
              return (
                <Link
                  key={entry.label}
                  to={entry.path}
                  className="mobile-nav__link"
                  onClick={closeMobileMenu}
                >
                  {entry.label}
                </Link>
              );
            }
            const isOpen = openMobileGroups.has(entry.label);
            const panelId = `mobile-nav-group-${entry.label.replace(/\s+/g, "-").toLowerCase()}`;
            return (
              <div key={entry.label} className="mobile-nav__group">
                <button
                  type="button"
                  className="mobile-nav__group-trigger"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => toggleMobileGroup(entry.label)}
                >
                  <span>
                    {entry.icon && (
                      <FontAwesomeIcon
                        icon={entry.icon}
                        style={{ marginRight: "8px", width: "16px" }}
                      />
                    )}
                    {entry.label}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`nav-group__chevron${isOpen ? " is-open" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div id={panelId} className="mobile-nav__group-items">
                    {entry.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="mobile-nav__sublink"
                        onClick={closeMobileMenu}
                      >
                        {item.icon && (
                          <FontAwesomeIcon
                            icon={item.icon}
                            style={{ marginRight: "8px", width: "16px" }}
                          />
                        )}
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      )}
    </div>
  );
}

export default Header;
